import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';

// Module-level state
let currentDate: string = '';
let converting = false;    // km/miles conversion guard (run cards)
let newCardCounter = 0;
let hiddenListenerAttached = false;

interface RunData {
    runs_id: number;
    km: number | null;
    miles: number | null;
    time: string | null;
    incline: number | null;
}

function formatModalDate(date: string): string {
    return new Date(date + 'T00:00:00').toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function initRunsModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }
}

export function openRunsModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    get('?do=getRunsFromDate&date=' + date, {}, (data: any) => {
        const runsData: RunData[] = data.status === 1 ? (data.runs || []) : [];

        const title = document.getElementById('chart-edit-modal-title');
        if (title) {
            title.textContent = 'Runs \u2014 ' + formatModalDate(date);
        }

        const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
        if (footer) footer.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (!body) return;
        body.innerHTML = '';

        // Container for all cards
        const container = document.createElement('div');
        container.className = 'activity-cards-container';
        body.appendChild(container);

        // Render existing run cards
        for (const run of runsData) {
            renderRunCard(container, run);
        }

        // Add button row — runs only
        const addRow = document.createElement('div');
        addRow.className = 'd-flex gap-2 mt-2';

        const addRunBtn = document.createElement('button');
        addRunBtn.type = 'button';
        addRunBtn.className = 'btn btn-outline-primary flex-fill';
        addRunBtn.textContent = '+ Run';
        addRunBtn.addEventListener('click', () => {
            const card = renderRunCard(container, null);
            container.appendChild(card);
            card.scrollIntoView({ behavior: 'smooth' });
        });

        addRow.appendChild(addRunBtn);
        body.appendChild(addRow);

        // Save All button
        const saveAllBtn = document.createElement('button');
        saveAllBtn.type = 'button';
        saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-activity-save-all';
        saveAllBtn.innerHTML = '<span class="js-activity-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-activity-spinner" role="status"></span>';
        saveAllBtn.addEventListener('click', () => handleSaveAll(saveAllBtn));
        body.appendChild(saveAllBtn);

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
        modalInstance.show();
    });
}

// ── Run cards ──────────────────────────────────────────────

function renderRunCard(container: HTMLElement, run: RunData | null): HTMLElement {
    const cardId = run ? 'run-card-' + run.runs_id : 'run-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 run-card';
    card.id = cardId;
    card.dataset.runsId = run ? String(run.runs_id) : '';
    card.dataset.type = 'run';

    const header = document.createElement('div');
    header.className = 'card-header d-flex justify-content-between align-items-center py-1 px-2';
    header.innerHTML = `<small class="text-primary fw-semibold">Run</small><button type="button" class="btn btn-sm btn-link text-danger p-0 js-card-delete" title="Delete">\u{1F5D1}</button>`;
    card.appendChild(header);

    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const kmVal = run?.km != null ? String(run.km) : '';
    const milesVal = run?.miles != null ? String(run.miles) : '';
    const timeVal = run?.time ?? '';
    const inclineVal = run?.incline != null ? String(run.incline) : '';

    cardBody.innerHTML = `
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">km</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-run-km" value="${kmVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">miles</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-run-miles" value="${milesVal}" />
            </div>
        </div>
        <div class="row g-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">time</label>
                <input type="text" class="form-control form-control-sm js-run-time" placeholder="HH:MM:SS" value="${timeVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">incline</label>
                <input type="number" step="0.1" inputmode="decimal" class="form-control form-control-sm js-run-incline" value="${inclineVal}" />
            </div>
        </div>
    `;
    card.appendChild(cardBody);

    const kmInput = card.querySelector('.js-run-km') as HTMLInputElement;
    const milesInput = card.querySelector('.js-run-miles') as HTMLInputElement;
    attachKmMilesSync(kmInput, milesInput);

    const deleteBtn = header.querySelector('.js-card-delete') as HTMLButtonElement;
    deleteBtn.addEventListener('click', () => handleDeleteRun(card, run));

    container.appendChild(card);
    return card;
}

function attachKmMilesSync(kmInput: HTMLInputElement, milesInput: HTMLInputElement): void {
    kmInput.addEventListener('input', () => {
        if (converting) return;
        converting = true;
        const km = parseFloat(kmInput.value);
        milesInput.value = !isNaN(km) ? (km / 1.60934).toFixed(3) : '';
        converting = false;
    });

    milesInput.addEventListener('input', () => {
        if (converting) return;
        converting = true;
        const miles = parseFloat(milesInput.value);
        kmInput.value = !isNaN(miles) ? (miles * 1.60934).toFixed(3) : '';
        converting = false;
    });
}

function handleDeleteRun(card: HTMLElement, run: RunData | null): void {
    if (!window.confirm('Delete this run?')) return;

    if (!run || !run.runs_id) {
        card.remove();
        return;
    }

    post('?do=deleteRun&runsId=' + run.runs_id, {}, (data: any) => {
        if (data.status === 1) {
            card.remove();
            if (data.new_km_sum !== undefined) {
                updateChartPoint(currentDate, data.new_km_sum);
            }
            notifier.success('Run deleted');
        } else {
            alert(data.message || 'Failed to delete run.');
        }
    });
}

// ── Save All ───────────────────────────────────────────────

function handleSaveAll(saveAllBtn: HTMLButtonElement): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-activity-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-activity-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    const prevError = modalEl.querySelector('.activity-inline-error');
    if (prevError) prevError.remove();

    const runCards = Array.from(document.querySelectorAll('#chart-edit-modal-body .run-card')) as HTMLElement[];

    if (runCards.length === 0) {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const runPromises: Promise<any>[] = runCards.map(card => {
        const runsIdAttr = card.dataset.runsId || '';
        const runsId = runsIdAttr !== '' ? parseInt(runsIdAttr) : null;
        const payload: Record<string, string | number> = {
            km: (card.querySelector('.js-run-km') as HTMLInputElement).value,
            miles: (card.querySelector('.js-run-miles') as HTMLInputElement).value,
            time: (card.querySelector('.js-run-time') as HTMLInputElement).value,
            incline: (card.querySelector('.js-run-incline') as HTMLInputElement).value,
        };
        if (runsId !== null && runsId > 0) payload.runs_id = runsId;
        return new Promise<any>((resolve, reject) => {
            post('?do=upsertRun&date=' + currentDate, payload, (data: any) => {
                data.status === 1 ? resolve(data) : reject(data.message || 'Save failed');
            });
        });
    });

    Promise.all(runPromises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update runs km chart point from last run result
        const lastRun = results[results.length - 1];
        if (lastRun?.new_km_sum !== undefined) {
            updateChartPoint(currentDate, lastRun.new_km_sum);
        }

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 activity-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

// ── Chart update ───────────────────────────────────────────

function updateChartPoint(date: string, newKmSum: number | null): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_km');
    if (!chart) return;

    const ts = Date.parse(date + 'T00:00:00');
    const series = chart.series[0];
    if (!series) return;

    const point = series.points.find((p: Highcharts.Point) => p.x === ts);
    if (point) {
        //@ts-ignore
        point.update(newKmSum);
    }
}
